import {IOnlineCondolenceSummary} from "../../types/resources/OnlineCondolenceSummary";
import OnlineCondolenceUtils from "../../utils/OnlineCondolenceUtils";
import {useTranslation} from "react-i18next";
import Title from './Title';
import LifeDates from './LifeDates';
import Stats from './Stats';
import Panel from '../Panel';
import ImageBlock from '../ImageBlock';
import {ImageSize} from '../../enums/ImageSize';
import {useSettingsContext} from '../../context/settings/useSettingsContext';
import ConditionalLink from '../ConditionalLink';
import styled from 'styled-components';
import React from 'react';
import { CondoListViewMode } from '../../enums/CondoListViewMode';
import { ILocation } from '../../types/resources/Location';
import ImageCard from '../ImageCard';

const CardWrapper = styled.div.attrs({
    className: 'cr-shadow cr-text-primary cr-rounded-block cr-w-100 cr-relative'
})`
    align-items: stretch;
    background-color: var(--cr-grey-light-color);
    display: grid;
    grid-template-columns: 1fr 4fr;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

type TImageProps = {
    $imageUrl?: string;
};

const Image = styled.div<TImageProps>`
    background-image: ${(props) => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
    background-position: center;
    background-size: cover;
    border-top-left-radius: var(--cr-block-rounding);
    border-bottom-left-radius: var(--cr-block-rounding);

    @media (max-width: 768px) {
        aspect-ratio: 16/9;
        background-image: ${(props) => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
        border-top-right-radius: var(--cr-block-rounding);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        //mask-image: linear-gradient(black, black), var(--cr-mask-outer);
        //mask-position: top, bottom;
        //mask-repeat: no-repeat;
        //mask-size: 100% calc(100% - 1rem), 100% 1rem;
    }
`;

const Right = styled.div`
    flex-wrap: wrap;
`

const CallToActionWrapper= styled.div`
    @media (max-width: 768px) {
        && {
            justify-content: flex-start;
        }
    }
`

const CallToAction = styled.div`
    @media (max-width: 768px) {
        && {
            font-size: smaller;
            margin-left: 0;
        }
    }
`

type TProps = {
    summary: IOnlineCondolenceSummary;
}

const OnlineCondolenceListItem = ({summary}: TProps) => {
    const {t} = useTranslation();
    const { publicUrl, condoFallbackImage, listViewMode } = useSettingsContext();

    const fullName = OnlineCondolenceUtils.getFullName(summary);
    const isCoverImage = (listViewMode === CondoListViewMode.Cover);

    const Container: React.FC<{ summary: IOnlineCondolenceSummary; children: React.ReactNode }> = ({summary, children}) => {
        const isCoverImage = (listViewMode === CondoListViewMode.Cover);

        return isCoverImage ? (
            <CardWrapper>
                {children}
            </CardWrapper>
        ) : (
            <Panel>
                {children}
            </Panel>
        );
    }

    return (
        <ConditionalLink to={summary.pageUrl ? `/${summary.pageUrl}` : null}>
            <Container summary={summary}>
                {isCoverImage &&
                    <Image className={'cr-image cr-aspect-video'} $imageUrl={
                        summary.imageFileUrl
                        || condoFallbackImage
                        || `${publicUrl}/placeholder-condolence.jpg`
                    } />
                    ||
                    <div className="cr-mr-xl">
                        <ImageBlock
                            src={
                                summary.imageFileUrl
                                || condoFallbackImage
                                || `${publicUrl}/placeholder-condolence.jpg`
                            }
                            placeholderSrc={
                                condoFallbackImage
                                || `${publicUrl}/placeholder-condolence.jpg`
                            }
                            alt={fullName}
                            size={ImageSize.Small}
                        />
                    </div>
                }
                <Right className={`cr-flex cr-gap cr-w-100 ${isCoverImage ? 'cr-p-xl' : ''}`}>
                    <div className="cr-flex cr-flex-1 cr-flex-col cr-gap cr-justify-end cr-w-100">
                        <Title fullName={fullName}/>
                        <LifeDates dateOfBirth={summary.dateOfBirth} dateOfDeath={summary.dateOfDeath}/>
                        <div className="cr-mt">
                            <Stats
                                numberOfCandles={summary.numberOfCandles}
                                numberOfPosts={summary.numberOfPosts}
                                numberOfLikes={summary.numberOfLikes}
                            />
                        </div>
                    </div>
                    <CallToActionWrapper className="cr-flex cr-flex-1 cr-center cr-w-100">
                        <CallToAction className={`cr-express-condolences cr-pill cr-ml-auto cr-text-secondary cr-border-secondary cr-upper ${isCoverImage ? '' : 'cr-mr-xl'}`}>
                            {t('express-condolences')}
                        </CallToAction>
                    </CallToActionWrapper>
                </Right>
            </Container>
        </ConditionalLink>
    )
}

export default OnlineCondolenceListItem;
